/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {CSSProperties} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import TombstoneLoader from './TombstoneLoader';

export interface SkeletonLoaderProps {
  width?: number | string;
  height?: number | string;
  className?: string;
  styles?: CSSProperties;
  round?: boolean;
  borderRadius?: number | string;
}

const useStyles = makeStyles(theme => ({
  root : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    width          : ({width}: SkeletonLoaderProps) => width || '100%',
    height         : ({height}: SkeletonLoaderProps) => height || '100%',
    borderRadius   : ({round, borderRadius}: SkeletonLoaderProps) => round ? '50%' : borderRadius || '2px',
    overflow       : 'hidden'
  }
}));

/**
 * @SkeletonLoader
 * 
 * The SkeletonLoader component provides a placeholder representation for content
 * that is yet to load. It helps in enhancing the perceived performance and provides
 * better user experience during content loading times.
 * 
 * Props:
 * 
 * - `width` (optional): Specifies the width of the skeleton. Defaults to '100%'.
 * - `height` (optional): Specifies the height of the skeleton. Defaults to '100%'.
 * - `className` (optional): Custom class to be added to the skeleton component.
 * - `styles` (optional): Custom styles to be applied to the skeleton component.
 * - `round` (optional): If set to true, makes the skeleton shape a circle. 
 *    Useful for avatar or profile picture placeholders.
 * - `borderRadius` (optional): Applies a border-radius to the skeleton. Defaults to '2px'. 
 *    This prop is overridden if `round` is set to true.
 * 
 * Usage:
 * 
 * - For a rectangle placeholder:
 *   <SkeletonLoader width="200px" height="50px" />
 * 
 * - For a circle placeholder (like for avatars):
 *   <SkeletonLoader width="50px" height="50px" round={true} />
 * 
 * - For a rectangle placeholder with custom border-radius:
 *   <SkeletonLoader width="200px" height="50px" borderRadius="8px" />
 * 
 * Note: Adjust the width and height according to the content you're simulating. 
 * The component is flexible enough to simulate various content shapes.
 * 
 */
export const SkeletonLoader = ({
  width = '100%',
  height = '100%',
  className,
  styles,
  round = false,
  borderRadius = '2px'
}: SkeletonLoaderProps) => {
  const classes = useStyles({width, height, round, borderRadius});

  return (
    <div className={`${classes.root} ${className || ''}`} style={styles}>
      <TombstoneLoader />
    </div>
  );
};

export default SkeletonLoader;
